import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { showNotications } from "../../../../../../../redux/notification/actions";
import { showError } from "../../../../../../../redux/common/actions";
import {
	downloadModelTaskPartsImportTemplate,
	importModelTaskParts,
} from "services/models/modelDetails/modelTaskParts";

const EMPTY_DRAFT = { name: "", qty: "", stockNumber: "", description: "" };

const hasErrors = (row) => Array.isArray(row.errors) && row.errors.length > 0;

/**
 * Owns the CSV-parts-import dialog's whole state machine: upload/dry-run,
 * CSV row selection, inline CSV-to-manual editing, and confirm-import.
 * The component is left to render this state, not manage it.
 */
const useCsvImportPreview = ({ taskId, importSuccess, handleClose }) => {
	const dispatch = useDispatch();

	// upload step state
	const [uploadPercentCompleted, setUploadPercentCompleted] = useState(0);
	const [loading, setLoading] = useState(false);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [showProgress, setShowProgress] = useState(true);
	const [fileError, setFileError] = useState(null);
	const cancelFileUpload = useRef(null);
	const isCancelled = useRef(false);

	// preview step state
	const [view, setView] = useState("upload");
	const [previewRows, setPreviewRows] = useState([]);
	const [selectedIndices, setSelectedIndices] = useState(new Set());
	const [convertedCsvIndices, setConvertedCsvIndices] = useState(new Set());
	const [s3Key, setS3Key] = useState(null);

	// manual rows (added or edited-from-csv)
	const [manualRows, setManualRows] = useState([]);

	// inline editing
	const [editingRow, setEditingRow] = useState(null); // { source: 'csv'|'manual', index: number, isNew: boolean }
	const [draftValues, setDraftValues] = useState(EMPTY_DRAFT);

	const resetState = () => {
		setView("upload");
		setFileError(null);
		setPreviewRows([]);
		setSelectedIndices(new Set());
		setConvertedCsvIndices(new Set());
		setS3Key(null);
		setManualRows([]);
		setEditingRow(null);
		setDraftValues(EMPTY_DRAFT);
		setUploadPercentCompleted(0);
		setLoading(false);
		setFetchLoading(false);
		setShowProgress(true);
	};

	const handleCancelFileUpload = () => {
		if (cancelFileUpload.current) {
			cancelFileUpload.current("Cancelled File import");
			isCancelled.current = true;
			dispatch(
				showNotications({
					show: true,
					message: "Cancelled File import",
					severity: "error",
				})
			);
		}
	};

	const closeOverride = () => {
		if (view === "upload") handleCancelFileUpload();
		handleClose();
		resetState();
	};

	// ── Download template ────────────────────────────────────────────────────

	const downloadTemplate = async () => {
		try {
			const response = await downloadModelTaskPartsImportTemplate(taskId);
			if (response.status && response.data) {
				const element = document.createElement("a");
				const file = new Blob([response.data], { type: "text/csv" });
				element.href = URL.createObjectURL(file);
				element.download = "TaskParts.csv";
				document.body.appendChild(element);
				element.click();
				document.body.removeChild(element);
				URL.revokeObjectURL(element.href);
			}
		} catch {
			dispatch(showError("Failed to download CSV template"));
		}
	};

	// ── Upload + dry-run ─────────────────────────────────────────────────────

	const onDocumentUpload = async (key, url, res) => {
		isCancelled.current = false;
		setFileError(null);
		if (!res) {
			setUploadPercentCompleted(0);
			setLoading(false);
			return;
		}
		setFetchLoading(true);
		try {
			const response = await importModelTaskParts(taskId, {
				Key: key,
				Import: false,
			});
			if (isCancelled.current) return;
			if (response.status) {
				const rows = Array.isArray(response.data)
					? response.data
					: (response.data?.rows ?? []);
				const selectable = rows
					.map((_, i) => i)
					.filter((i) => !hasErrors(rows[i]));
				setPreviewRows(rows);
				setSelectedIndices(new Set(selectable));
				setS3Key(key);
				setView("preview");
			} else {
				setFileError(
					response?.data?.detail ?? "The file could not be processed."
				);
			}
		} catch {
			setFileError("The file could not be processed.");
		} finally {
			setFetchLoading(false);
			setUploadPercentCompleted(0);
			setLoading(false);
			setShowProgress(true);
		}
	};

	// ── Confirm import ────────────────────────────────────────────────────────

	const handleConfirmImport = async () => {
		setFetchLoading(true);
		try {
			const response = await importModelTaskParts(taskId, {
				Key: s3Key,
				Import: true,
				SelectedIndices: [...selectedIndices].sort((a, b) => a - b),
				ManualRows: manualRows.map((row) => ({
					name: row.name,
					description: row.description,
					qty: Number(row.qty),
					stockNumber: row.stockNumber,
				})),
			});
			if (response.status) {
				await importSuccess();
				closeOverride();
			} else {
				dispatch(showError(response?.data?.detail || "Failed To Import"));
			}
		} catch {
			dispatch(showError("Failed To Import"));
		} finally {
			setFetchLoading(false);
		}
	};

	// ── CSV row selection ─────────────────────────────────────────────────────

	const selectableIndices = useMemo(
		() =>
			previewRows
				.map((_, i) => i)
				.filter((i) => !hasErrors(previewRows[i]) && !convertedCsvIndices.has(i)),
		[previewRows, convertedCsvIndices]
	);

	const handleToggleRow = (index) => {
		if (hasErrors(previewRows[index]) || convertedCsvIndices.has(index)) return;
		setSelectedIndices((prev) => {
			const next = new Set(prev);
			next.has(index) ? next.delete(index) : next.add(index);
			return next;
		});
	};

	const handleToggleAll = () => {
		setSelectedIndices((prev) =>
			prev.size === selectableIndices.length
				? new Set()
				: new Set(selectableIndices)
		);
	};

	// ── Inline editing ────────────────────────────────────────────────────────

	const startEditCsvRow = (index) => {
		const row = previewRows[index];
		setDraftValues({
			name: row.name ?? "",
			qty: row.qty ?? "",
			stockNumber: row.stockNumber ?? "",
			description: row.description ?? "",
		});
		setEditingRow({ source: "csv", index, isNew: false });
	};

	const startEditManualRow = (index) => {
		const row = manualRows[index];
		setDraftValues({
			name: row.name ?? "",
			qty: row.qty ?? "",
			stockNumber: row.stockNumber ?? "",
			description: row.description ?? "",
		});
		setEditingRow({ source: "manual", index, isNew: false });
	};

	const handleAddManualRow = () => {
		const newIndex = manualRows.length;
		setManualRows((prev) => [
			...prev,
			{ name: "", qty: "", stockNumber: "", description: "" },
		]);
		setDraftValues(EMPTY_DRAFT);
		setEditingRow({ source: "manual", index: newIndex, isNew: true });
	};

	const handleSaveEdit = () => {
		if (!editingRow) return;
		if (editingRow.source === "csv") {
			setManualRows((prev) => [...prev, { ...draftValues }]);
			setConvertedCsvIndices((prev) => new Set([...prev, editingRow.index]));
			setSelectedIndices((prev) => {
				const next = new Set(prev);
				next.delete(editingRow.index);
				return next;
			});
		} else {
			setManualRows((prev) =>
				prev.map((r, i) => (i === editingRow.index ? { ...draftValues } : r))
			);
		}
		setEditingRow(null);
		setDraftValues(EMPTY_DRAFT);
	};

	const handleCancelEdit = () => {
		if (editingRow?.isNew) {
			setManualRows((prev) => prev.slice(0, -1));
		}
		setEditingRow(null);
		setDraftValues(EMPTY_DRAFT);
	};

	const handleDeleteManualRow = (index) => {
		setManualRows((prev) => prev.filter((_, i) => i !== index));
		if (editingRow?.source === "manual") {
			if (editingRow.index === index) {
				setEditingRow(null);
				setDraftValues(EMPTY_DRAFT);
			} else if (index < editingRow.index) {
				setEditingRow((prev) => ({ ...prev, index: prev.index - 1 }));
			}
		}
	};

	useEffect(() => {
		if (uploadPercentCompleted === 100) setShowProgress(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadPercentCompleted]);

	// ── Derived flags ─────────────────────────────────────────────────────────

	const isEditing = editingRow !== null;
	const allSelected =
		selectableIndices.length > 0 &&
		selectedIndices.size === selectableIndices.length;
	const someSelected =
		selectedIndices.size > 0 && selectedIndices.size < selectableIndices.length;
	// The newly added row is pushed into manualRows before it's saved, so exclude
	// it here to avoid counting an unsaved row in the visible selection totals.
	const savedManualRowsCount = manualRows.length - (editingRow?.isNew ? 1 : 0);
	const canImport =
		!isEditing && (selectedIndices.size > 0 || manualRows.length > 0);

	return {
		// upload state
		uploadPercentCompleted,
		setUploadPercentCompleted,
		loading,
		setLoading,
		fetchLoading,
		showProgress,
		fileError,
		cancelFileUpload,
		view,
		onDocumentUpload,
		downloadTemplate,
		handleCancelFileUpload,
		closeOverride,
		resetState,

		// preview state
		previewRows,
		manualRows,
		convertedCsvIndices,
		selectedIndices,
		editingRow,
		draftValues,
		setDraftValues,

		// derived
		selectableIndices,
		isEditing,
		allSelected,
		someSelected,
		savedManualRowsCount,
		canImport,

		// handlers
		handleToggleRow,
		handleToggleAll,
		startEditCsvRow,
		startEditManualRow,
		handleAddManualRow,
		handleSaveEdit,
		handleCancelEdit,
		handleDeleteManualRow,
		handleConfirmImport,
	};
};

export default useCsvImportPreview;
